import React, { useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { TourOption, useGetTourOptions } from '~/hooks/apis/tours/useTourSearch';
import useDebounced from '~/hooks/useDebounced';

interface Props {
  value: TourOption | null;
  onChange: (tour: TourOption | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

export default function TourPicker({ value, onChange, label = 'Tour', helperText, disabled }: Props) {
  const [inputValue, setInputValue] = useState('');

  // Debounced so a query is keyed on a settled term rather than one per keystroke.
  const searchString = useDebounced(inputValue, SEARCH_DEBOUNCE_MS);

  const { data: options = [], isFetching } = useGetTourOptions(searchString);

  // Kept in the list so the field does not clear itself as the search changes.
  const mergedOptions = useMemo(() => {
    if (!value || options.some((option) => option.id === value.id)) {
      return options;
    }
    return [value, ...options];
  }, [options, value]);

  return (
    <Autocomplete
      value={value}
      onChange={(_event, tour) => onChange(tour)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={mergedOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={isFetching}
      disabled={disabled}
      filterOptions={(all) => all}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
